import { useState } from "react";
import { Search, Loader2, Fingerprint, Ban } from "lucide-react";
import { useLazySearchFirmPartnersQuery } from "../api/firmApi";

/**
 * Member picker restricted to Members/Fellows (via /firm/partners/search).
 * When enforceEligible is true, members already in an approved firm are shown
 * but greyed out (used for partners); when false they are selectable (referees).
 */
export default function MemberSearchModal({
  title, onPick, onClose, existing = [], enforceEligible = true,
}: {
  title: string;
  onPick: (m: any) => void;
  onClose: () => void;
  existing?: string[];
  enforceEligible?: boolean;
}) {
  const [q, setQ] = useState("");
  const [trigger, { data = [], isFetching }] = useLazySearchFirmPartnersQuery();

  const inp = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
        <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) trigger(q.trim()); }} className="flex gap-2 mb-4">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or membership number" className={inp} />
          <button type="submit" className="px-4 rounded-lg bg-gray-900 text-white flex items-center text-sm">
            {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </form>
        <p className="text-xs text-gray-400 mb-2">Only Members and Fellows appear here.</p>
        <div className="max-h-72 overflow-y-auto divide-y">
          {data.length === 0 && !isFetching && (
            <p className="text-sm text-gray-400 py-6 text-center">No results yet — search above.</p>
          )}
          {data.map((m: any) => {
            const already = existing.includes(m.member_id);
            const blocked = enforceEligible && !m.eligible;
            const disabled = already || blocked;
            return (
              <button key={m.member_id} type="button" disabled={disabled}
                onClick={() => { onPick(m); onClose(); }}
                className={`w-full flex items-center gap-3 py-3 px-2 text-left ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"}`}>
                <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold shrink-0">
                  {m.surname?.[0] ?? "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {[m.surname, m.firstname, m.othernames].filter(Boolean).join(" ")}
                  </p>
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <Fingerprint className="w-3 h-3" /> {m.membership_no ?? m.member_id} · {m.grade}
                  </span>
                </div>
                {already && <span className="text-xs text-gray-400">added</span>}
                {blocked && <span className="flex items-center gap-1 text-xs text-red-500"><Ban className="w-3 h-3" /> in {m.blocked_by}</span>}
              </button>
            );
          })}
        </div>
        <div className="flex justify-end mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}
